/**
 * One automatic sign-in per browser session.
 *
 * ── The outage this exists to stop ──
 * `msal-react@5` was installed against `msal-browser@3`, two majors below its peer requirement. v5's
 * `MsalProvider` does not call `initialize()` or `handleRedirectPromise()` — msal-browser v5 handles
 * that itself — so on v3 the redirect coming back from Entra was never processed and `accounts` stayed
 * empty forever.
 *
 * The gate reacted to that the only way it could: settled, no account, start sign-in. Entra still had a
 * session, bounced the user straight back, the account was still not there, and round it went — five or
 * six times until Entra throttled and dropped them on the sign-in screen. Signing in was impossible and
 * the screen never said why.
 *
 * The version pin fixes the cause. This fixes the SHAPE: an automatic redirect that can fire again on
 * the page it lands on is a loop waiting for a reason, and the next reason will not be this one.
 *
 * ── Why sessionStorage ──
 * The redirect IS a page load. A module-scoped flag resets on the way back and bounds nothing. Scoped
 * to the tab, so a genuinely new session still gets its attempt. Same reasoning, same mechanism as the
 * 401 bound in apiClient — deliberately, because they are the same failure.
 *
 * ── Why a failed probe means DO NOT redirect ──
 * If the attempt cannot be recorded, it cannot be bounded, and an unbounded automatic redirect is the
 * outage. Showing the user an error beats bouncing them until Entra gives up on them.
 */

const KEY = 'stride.signin-attempted';
/**
 * A SEPARATE key to test writability with.
 *
 * The first version probed by writing and removing `KEY` itself, which deleted the very mark it was
 * checking for. `AuthGate` asks `!canRecordAttempt() || signinAlreadyAttempted()`, so the probe wiped
 * the mark and the check that followed always answered "no attempt yet" — an unbounded redirect, which
 * is the outage this file exists to prevent, reintroduced by its own guard. Seventeen trips to Entra in
 * thirty seconds, measured on production.
 *
 * The unit tests missed it because they exercised each function alone and never the SEQUENCE the caller
 * uses. There is now a test for exactly that sequence.
 */
const PROBE = 'stride.signin-attempted.probe';

/** True when this tab can remember an attempt — private modes and blocked storage can throw. */
export function canRecordAttempt(storage: Storage | undefined = safeStorage()): boolean {
  if (storage === undefined) return false;
  try {
    storage.setItem(PROBE, '1');
    storage.removeItem(PROBE);
    return true;
  } catch {
    return false;
  }
}

export function signinAlreadyAttempted(storage: Storage | undefined = safeStorage()): boolean {
  try {
    return storage?.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

export function markSigninAttempted(storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.setItem(KEY, '1');
  } catch {
    // Nothing to do: the caller has already checked it can record, and failing here must not throw
    // inside a render.
  }
}

/**
 * Called once the user is genuinely signed in, so a session that expires an hour later still gets its
 * own automatic attempt. The bound is against looping, not against ever signing in again.
 */
export function clearSigninAttempt(storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.removeItem(KEY);
  } catch {
    // As above.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined;   // accessing sessionStorage itself throws when site data is blocked
  }
}
